(() => {
  'use strict';
  const API_ORIGIN = 'https://app.songbond.com';
  const TOKEN = /^[A-Za-z0-9_-]{32,128}$/;
  const BRIEF = /^[A-Za-z0-9_-]{8,64}$/;
  const clock = seconds => {
    const value = Math.max(0, Math.floor(Number(seconds) || 0));
    return Math.floor(value / 60) + ':' + String(value % 60).padStart(2, '0');
  };
  function httpsURL(value) {
    if (typeof value !== 'string' || value.length > 8192) return null;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && !url.username && !url.password ? url : null;
    } catch (_) { return null; }
  }
  function orderURL(value) {
    const url = httpsURL(value);
    return url && url.origin === API_ORIGIN && /^\/orders\/[A-Za-z0-9_-]+\/[A-Za-z0-9_-]+\/?$/.test(url.pathname) ? url.href : null;
  }
  function checkoutItem(checkout) {
    if (!checkout || !Number.isSafeInteger(checkout.variant_id) || checkout.variant_id <= 0 || checkout.quantity !== 1) throw new Error('checkout');
    const props = checkout.properties;
    if (!props || typeof props !== 'object' || Array.isArray(props) || Object.keys(props).length > 40) throw new Error('checkout');
    if (Object.entries(props).some(([key, value]) => ['__proto__', 'constructor', 'prototype'].includes(key) || typeof value !== 'string')) throw new Error('checkout');
    if (!BRIEF.test(props._sb_brief_id || '') || props._sb_paid_presale !== '1' || props._sb_presale_role !== 'song' || props._sb_recovery !== '1' || Object.hasOwn(props, 'Gift Package')) throw new Error('checkout');
    const email = checkout.checkout_email;
    if (typeof email !== 'string' || email.length > 320 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || props._sb_delivery_email !== email) throw new Error('checkout');
    // The feed owns this brief: preserve its properties byte-for-byte, without folding cart labels into Story.
    return { id: checkout.variant_id, quantity: 1, properties: props };
  }
  function initialize(root) {
    if (root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';
    const copy = JSON.parse(root.querySelector('[data-rp-copy]').textContent);
    let prices = null;
    try { prices = JSON.parse(root.querySelector('[data-rp-prices]')?.textContent || 'null'); }
    catch (_) {}
    const audio = root.querySelector('[data-rp-audio]');
    const play = root.querySelector('[data-rp-play]');
    const seek = root.querySelector('[data-rp-seek]');
    const current = root.querySelector('[data-rp-current]');
    const total = root.querySelector('[data-rp-total]');
    const message = root.querySelector('[data-rp-audio-message]');
    const content = root.querySelector('[data-rp-content]');
    const status = root.querySelector('[data-rp-status]');
    const retry = root.querySelector('[data-rp-retry]');
    const order = root.querySelector('[data-rp-order]');
    const support = root.querySelector('[data-rp-support]');
    const sticky = root.querySelector('[data-rp-sticky]');
    const checkoutError = root.querySelector('[data-rp-checkout-error]');
    const unlocks = Array.from(root.querySelectorAll('[data-rp-unlock]'));
    const videos = Array.from(root.querySelectorAll('[data-rp-reaction]'));
    const tokens = new URL(window.location.href).searchParams.getAll('t');
    const token = tokens.length === 1 && TOKEN.test(tokens[0]) ? tokens[0] : null;
    const rootPath = new URL(root.dataset.root || '/', window.location.origin);
    const basePath = rootPath.origin === window.location.origin ? rootPath.pathname.replace(/\/?$/, '/') : '/';
    let state = 'loading', feed = null, feedRequest = null, feedController = null;
    let pollTimer = null, expiry = 0, busy = false, stopped = false, wasHiddenAt = 0;
    let limit = 90, ended = false, playPending = false, audioRefreshAttempts = 0;
    function clearPoll() { clearTimeout(pollTimer); pollTimer = null; }
    function currentVariant() {
      if (!feed || prices?.currency !== root.dataset.currency || !Array.isArray(prices?.variants)) return null;
      const variant = prices.variants.find(item => item.id === feed.checkout.variant_id);
      return variant && Number.isSafeInteger(variant.amount) && variant.amount >= 0 && typeof variant.formatted === 'string' && variant.formatted.trim() ? variant : null;
    }
    function unlockLabel() {
      const name = typeof feed?.recipient_name === 'string' ? feed.recipient_name.trim() : '';
      return name && name.length <= 32 && typeof copy.unlock_personal === 'string' ? copy.unlock_personal.replace('__NAME__', () => name) : copy.unlock;
    }
    function syncPrices() {
      const variant = currentVariant();
      root.querySelectorAll('[data-rp-price-group], [data-rp-total-row]').forEach(node => { node.hidden = !variant; });
      root.querySelectorAll('[data-rp-price]').forEach(node => { node.textContent = variant ? variant.formatted.trim() : ''; });
      const compare = variant && Number.isSafeInteger(variant.compare_amount) && variant.compare_amount > variant.amount && typeof variant.compare_formatted === 'string' && variant.compare_formatted.trim();
      root.querySelectorAll('[data-rp-compare]').forEach(node => {
        node.hidden = !compare;
        node.textContent = compare ? variant.compare_formatted.trim() : '';
      });
    }
    function syncButtons() {
      syncPrices();
      unlocks.forEach(button => {
        button.disabled = busy || state !== 'ready';
        button.setAttribute('aria-busy', String(busy));
        button.querySelector('[data-rp-unlock-label]').textContent = busy ? copy.opening : unlockLabel();
      });
    }
    function setCheckoutError(text) {
      checkoutError.textContent = text;
      checkoutError.hidden = !text;
      if (text) checkoutError.scrollIntoView({block: 'nearest', behavior: 'auto'});
    }
    function showState(nextState, orderLink) {
      clearPoll();
      state = nextState;
      root.dataset.state = nextState;
      const ready = state === 'ready';
      content.hidden = !ready;
      sticky.hidden = !ready;
      status.hidden = ready;
      status.setAttribute('aria-busy', String(state === 'loading' || state === 'pending'));
      root.querySelector('[data-rp-spinner]').hidden = !['loading', 'pending'].includes(state);
      retry.hidden = state !== 'network';
      order.hidden = state !== 'paid' || !orderLink;
      order.removeAttribute('href');
      if (!order.hidden) order.href = orderLink;
      support.hidden = !['paid', 'failed', 'invalid', 'unavailable'].includes(state);
      if (!ready) {
        root.querySelector('[data-rp-status-title]').textContent = copy[state + '_title'];
        root.querySelector('[data-rp-status-body]').textContent = copy[state + '_body'];
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
        play.disabled = true;
        seek.disabled = true;
        feed = null;
        expiry = 0;
        videos.forEach(video => video.pause());
      }
      syncButtons();
    }
    function sync() {
      const position = Math.min(limit, Math.max(0, audio.currentTime || 0));
      seek.value = String(position);
      seek.style.setProperty('--rp-progress', (position / limit * 100) + '%');
      const displayedPosition = ended ? Math.ceil(limit) : position;
      current.textContent = clock(displayedPosition);
      const playing = !audio.paused && !audio.ended;
      play.setAttribute('aria-pressed', String(playing));
      play.setAttribute('aria-label', playing ? copy.pause : ended ? copy.replay : copy.play);
      play.dataset.ended = String(ended && !playing);
      seek.setAttribute('aria-valuetext', clock(displayedPosition) + ' / ' + clock(Math.ceil(limit)));
    }
    function renderFeed(data) {
      if (!data || typeof data !== 'object') throw new Error('feed');
      if (data.state === 'ready') {
        const src = httpsURL(data.preview_url);
        const expires = Number(data.preview_expires_in);
        if (!src || data.preview_seconds !== 90 || !Number.isFinite(expires) || expires <= 0) throw new Error('feed');
        checkoutItem(data.checkout);
        const name = typeof data.recipient_name === 'string' ? data.recipient_name : '';
        const genre = typeof data.genre === 'string' ? data.genre : '';
        root.querySelector('[data-rp-heading]').textContent = name ? copy.heading.replace('__NAME__', () => name) : copy.generic_heading;
        root.querySelector('[data-rp-subtitle]').textContent = genre ? copy.subtitle.replace('__GENRE__', () => genre) : copy.personalSubtitle;
        root.querySelector('[data-rp-song-heading]').textContent = name ? copy.song_personal.replace('__NAME__', () => name) : copy.song_generic;
        showState('ready');
        feed = data;
        syncButtons();
        expiry = Date.now() + Math.min(expires, 900) * 1000;
        if (audio.getAttribute('src') !== data.preview_url) {
          audio.pause();
          audio.src = data.preview_url;
          audio.load();
          ended = false;
          limit = 90;
          seek.value = '0';
          seek.max = '90';
          seek.disabled = true;
          total.textContent = '1:30';
          message.textContent = '';
        }
        play.disabled = false;
        sync();
        return data;
      }
      if (data.state === 'pending') {
        showState('pending');
        // The contract currently specifies ten seconds. Never let a malformed interval hammer the API.
        const seconds = Math.max(10, Math.min(60, Number(data.poll_after_seconds) || 10));
        if (!document.hidden && !stopped) pollTimer = setTimeout(() => loadFeed(), seconds * 1000);
        return data;
      }
      if (data.state === 'paid') { showState('paid', orderURL(data.order_url)); return data; }
      if (data.state === 'failed' || data.state === 'invalid') { showState(data.state); return data; }
      throw new Error('feed');
    }
    async function loadFeed() {
      if (stopped || !token) return null;
      if (feedRequest) return feedRequest;
      clearPoll();
      feedController = new AbortController();
      const controller = feedController;
      const timeout = setTimeout(() => controller.abort(), 15000);
      feedRequest = (async () => {
        try {
          const response = await fetch(API_ORIGIN + '/api/shopify/preview/' + encodeURIComponent(token), {
            method: 'GET', mode: 'cors', credentials: 'omit', headers: {Accept: 'application/json'},
            cache: 'no-store', referrerPolicy: 'no-referrer', redirect: 'error', signal: controller.signal
          });
          if (stopped) return null;
          if (response.status === 404) { showState('invalid'); return {state: 'invalid'}; }
          if (!response.ok) throw new Error('network');
          return renderFeed(await response.json());
        } catch (_) {
          if (!stopped) showState('network');
          return null;
        } finally {
          clearTimeout(timeout);
          feedRequest = null;
          if (feedController === controller) feedController = null;
        }
      })();
      return feedRequest;
    }
    async function freshFeed() {
      if (feedRequest) await feedRequest;
      return loadFeed();
    }
    async function request(path, body) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(basePath + path, {
          method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
          headers: {Accept: 'application/json', ...(body ? {'Content-Type': 'application/json'} : {})},
          ...(body ? {body: JSON.stringify(body)} : {}), signal: controller.signal
        });
        if (!response.ok) throw new Error('cart');
        return await response.json();
      } finally { clearTimeout(timeout); }
    }
    async function unlock() {
      if (busy || state !== 'ready' || !token) return;
      busy = true;
      setCheckoutError('');
      syncButtons();
      audio.pause();
      videos.forEach(video => video.pause());
      try {
        // Recheck payment and checkout details immediately before touching the cart.
        const latest = await freshFeed();
        if (!latest || latest.state !== 'ready') return;
        const item = checkoutItem(latest.checkout);
        if (!window.SB_PRESALE || typeof window.SB_PRESALE.reconcileCart !== 'function') throw new Error('cart');
        const work = () => window.SB_PRESALE.reconcileCart({
          items: [item], briefId: item.properties._sb_brief_id, request,
          currency: root.dataset.currency, errorMessage: copy.checkout_error
        });
        if (navigator.locks && typeof navigator.locks.request === 'function') {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 25000);
          try { await navigator.locks.request('songbond-paid-presale-cart', {signal: controller.signal}, work); }
          finally { clearTimeout(timeout); }
        } else await work(); // The in-page busy guard still prevents duplicate clicks on older browsers.
        const checkoutURL = new URL(basePath + 'checkout', window.location.origin);
        checkoutURL.searchParams.set('checkout[email]', latest.checkout.checkout_email);
        window.location.assign(checkoutURL.href);
      } catch (_) {
        if (state === 'ready') setCheckoutError(copy.checkout_error);
      } finally {
        busy = false;
        syncButtons();
      }
    }
    audio.addEventListener('loadedmetadata', () => {
      if (state !== 'ready') return;
      if (Number.isFinite(audio.duration) && audio.duration > 0) limit = Math.min(90, audio.duration);
      seek.max = String(limit);
      seek.disabled = false;
      total.textContent = clock(Math.ceil(limit));
      sync();
    });
    audio.addEventListener('timeupdate', () => {
      if (audio.currentTime >= limit) {
        audio.pause();
        ended = true;
        message.textContent = copy.end;
      }
      sync();
    });
    audio.addEventListener('play', sync);
    audio.addEventListener('pause', sync);
    audio.addEventListener('ended', () => { ended = true; message.textContent = copy.end; sync(); });
    audio.addEventListener('error', async () => {
      if (state !== 'ready' || stopped) return;
      message.textContent = copy.player_error;
      play.disabled = false;
      if (audioRefreshAttempts === 0) {
        audioRefreshAttempts++;
        await freshFeed();
      }
      sync();
    });
    play.addEventListener('click', async () => {
      if (state !== 'ready' || playPending) return;
      if (!audio.paused) { audio.pause(); return; }
      videos.forEach(video => video.pause());
      message.textContent = '';
      playPending = true;
      play.disabled = true;
      try {
        if (Date.now() >= expiry - 5000) {
          const refreshed = await freshFeed();
          if (!refreshed || refreshed.state !== 'ready') return;
        }
        if (ended || audio.currentTime >= limit) { audio.currentTime = 0; ended = false; }
        await audio.play();
      } catch (_) { if (state === 'ready') message.textContent = copy.player_error; }
      finally { playPending = false; play.disabled = state !== 'ready'; sync(); }
    });
    seek.addEventListener('input', () => {
      const position = Number(seek.value);
      if (!Number.isFinite(position) || state !== 'ready') return;
      audio.currentTime = Math.min(limit, Math.max(0, position));
      ended = false;
      message.textContent = '';
      sync();
    });
    unlocks.forEach(button => button.addEventListener('click', unlock));
    retry.addEventListener('click', () => { showState('loading'); loadFeed(); });
    function loadVideo(video) {
      if (!video.getAttribute('src') && video.dataset.src) video.src = video.dataset.src;
    }
    videos.forEach(video => {
      video.addEventListener('play', () => {
        audio.pause();
        videos.forEach(other => { if (other !== video) other.pause(); });
      });
      video.addEventListener('pointerdown', () => loadVideo(video), {once: true});
      video.addEventListener('focus', () => loadVideo(video), {once: true});
    });
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) loadVideo(entry.target);
          else entry.target.pause();
        });
      }, {rootMargin: '200px 0px'});
      videos.forEach(video => observer.observe(video));
    } else videos.forEach(loadVideo);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        wasHiddenAt = Date.now();
        clearPoll();
        audio.pause();
        videos.forEach(video => video.pause());
      } else if (!stopped && token && !window.Shopify?.designMode && (state === 'pending' || (state === 'ready' && (Date.now() >= expiry - 5000 || Date.now() - wasHiddenAt > 15000)))) {
        loadFeed();
      }
    });
    window.addEventListener('pagehide', () => {
      stopped = true;
      clearPoll();
      if (feedController) feedController.abort();
      audio.pause();
      videos.forEach(video => video.pause());
    });
    window.addEventListener('pageshow', event => {
      if (!event.persisted) return;
      stopped = false;
      busy = false;
      syncButtons();
      if (token && !window.Shopify?.designMode && ['ready', 'pending', 'loading', 'network'].includes(state)) freshFeed();
    });
    if (window.Shopify?.designMode) showState('unavailable');
    else if (!token) showState('invalid');
    else { showState('loading'); loadFeed(); }
  }
  document.querySelectorAll('[data-sb-recovery]').forEach(initialize);
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll('[data-sb-recovery]').forEach(initialize);
  });
})();
